import type { BirthInputDraft, BirthInputValidationIssue } from './birthInput.js';
import type { TopicId } from './rules.js';
import type { RelationshipContextType } from './relationshipContext.js';
import { DEFAULT_NATAL_TOPICS } from './interpretation.js';
import type { ApplicationWorkflowOptions, CalculationEngine, RelationshipWorkflowSuccess } from './applicationWorkflow.js';
import { runRelationshipWorkflow } from './applicationWorkflow.js';
import { runProductionHoroscopeWorkflow, runProductionNatalWorkflow } from './productionWorkflow.js';
import { buildProductionNatalResultPageViewModel, type ProductionNatalResultPageViewModel } from './productionResultPresentation.js';
import { buildProductionChartPageViewModel, type ProductionChartPageViewModel } from './productionChartPagePresentation.js';
import { buildProductionTimingPageViewModel, type ProductionTimingPageViewModel } from './productionTimingPresentation.js';
import { buildOverviewDigest } from './overviewDigest.js';
import { buildResultActionsViewModel } from './resultActionsPresentation.js';
import { relationshipReportViewModel, type RelationshipReportViewModel } from './relationshipPresentation.js';
import type { LocalChartRepository } from './localPersistence.js';
import { saveProductionNatalChart, type SaveProductionNatalChartResult } from './productionLocalPersistence.js';
import { buildProductionPrintPacket, type ProductionPrintActionId } from './productionPrintCommand.js';
import type { PrintPacket } from './printPresentation.js';
import {
  createInitialAppNavigationState,
  navigateApp,
  setNatalResultAvailability,
  type AppNavigationState,
  type AppScreenId,
  setRelationshipResultAvailability,
} from './appNavigation.js';

type ProductionNatalWorkflowResult = Awaited<ReturnType<typeof runProductionNatalWorkflow>>;
type ProductionNatalWorkflowSuccess = Extract<ProductionNatalWorkflowResult, { ok: true }>;

export interface ProductionAppSnapshot {
  model: 'PRODUCTION-APP-CONTROLLER-0.1.0';
  navigation: AppNavigationState;
  natalIssues: readonly BirthInputValidationIssue[];
  natalChart?: ProductionChartPageViewModel;
  natalResult?: ProductionNatalResultPageViewModel;
  timingIssues: readonly BirthInputValidationIssue[];
  timingResult?: ProductionTimingPageViewModel;
  relationshipIssues: Readonly<{ personA: readonly BirthInputValidationIssue[]; personB: readonly BirthInputValidationIssue[] }>;
  relationshipResult?: RelationshipReportViewModel;
  disclosure: string;
}

export interface ProductionAppNavigationResult {
  allowed: boolean;
  snapshot: ProductionAppSnapshot;
  reason?: string;
}

export class ProductionAppController {
  #navigation = createInitialAppNavigationState();
  #natalResult: ProductionNatalWorkflowSuccess | undefined;
  #natalInput: BirthInputDraft | undefined;
  #timingResult: Extract<Awaited<ReturnType<typeof runProductionHoroscopeWorkflow>>, { ok: true }> | undefined;
  #timingIssues: readonly BirthInputValidationIssue[] = Object.freeze([]);
  #natalIssues: readonly BirthInputValidationIssue[] = Object.freeze([]);
  #relationshipResult: RelationshipWorkflowSuccess | undefined;
  #relationshipIssues: Readonly<{ personA: readonly BirthInputValidationIssue[]; personB: readonly BirthInputValidationIssue[] }> = Object.freeze({ personA: Object.freeze([]), personB: Object.freeze([]) });

  snapshot(): ProductionAppSnapshot {
    const chartView = this.#natalResult ? buildProductionChartPageViewModel(this.#natalResult.chart) : undefined;
    const resultView = this.#natalResult ? this.#buildNatalResultView(this.#natalResult) : undefined;
    const timingChronology = this.#timingResult?.chronology?.target ? Object.freeze({ birth: this.#timingResult.chronology.birth, target: this.#timingResult.chronology.target }) : undefined;
    const timingView = this.#timingResult ? buildProductionTimingPageViewModel(this.#timingResult.chart, this.#timingResult.horoscope, this.#timingResult.horoscopeReport, timingChronology) : undefined;
    const relationshipView = this.#relationshipResult ? relationshipReportViewModel(this.#relationshipResult.report, this.#relationshipResult.chronology, this.#relationshipResult.context) : undefined;
    return Object.freeze({
      model: 'PRODUCTION-APP-CONTROLLER-0.1.0' as const,
      navigation: this.#navigation,
      natalIssues: this.#natalIssues,
      ...(chartView ? { natalChart: chartView } : {}),
      ...(resultView ? { natalResult: resultView } : {}),
      timingIssues: this.#timingIssues,
      ...(timingView ? { timingResult: timingView } : {}),
      relationshipIssues: this.#relationshipIssues,
      ...(relationshipView ? { relationshipResult: relationshipView } : {}),
      disclosure: 'App Controller 只對 UI 暴露已審計的呈現資料；原始命盤只存在目前瀏覽器記憶體中的私有 workflow 狀態，不進導航狀態。',
    });
  }

  navigate(target: AppScreenId): ProductionAppNavigationResult {
    const transition = navigateApp(this.#navigation, target);
    if (transition.allowed) this.#navigation = transition.state;
    return Object.freeze({
      allowed: transition.allowed,
      snapshot: this.snapshot(),
      ...(transition.reason ? { reason: transition.reason } : {}),
    });
  }

  async submitNatal(
    input: BirthInputDraft,
    engine: CalculationEngine,
    topics: readonly TopicId[] = DEFAULT_NATAL_TOPICS,
    options: ApplicationWorkflowOptions = {},
  ): Promise<ProductionAppSnapshot> {
    const result = await runProductionNatalWorkflow(input, engine, topics, options);
    if (!result.ok) {
      this.#natalResult = undefined;
      this.#natalInput = undefined;
      this.#timingResult = undefined;
      this.#timingIssues = Object.freeze([]);
      this.#natalIssues = Object.freeze([...result.issues]);
      this.#navigation = setNatalResultAvailability(this.#navigation, false);
      this.#navigation = navigateApp(this.#navigation, 'birth_input').state;
      return this.snapshot();
    }

    this.#natalResult = result;
    this.#natalInput = Object.freeze({ ...input });
    this.#timingResult = undefined;
    this.#timingIssues = Object.freeze([]);
    this.#natalIssues = Object.freeze([]);
    this.#navigation = setNatalResultAvailability(this.#navigation, true);
    this.#navigation = navigateApp(this.#navigation, 'natal_chart').state;
    return this.snapshot();
  }

  async submitNatalTiming(
    targetDate: string,
    targetTimeIndex: number,
    engine: CalculationEngine,
    natalTopics: readonly TopicId[] = DEFAULT_NATAL_TOPICS,
    horoscopeTopics: readonly TopicId[] = ['timing'],
    options: ApplicationWorkflowOptions = {},
  ): Promise<ProductionAppSnapshot> {
    if (!this.#natalInput || !this.#natalResult) throw new Error('Natal timing requires a successful natal result first');
    const result = await runProductionHoroscopeWorkflow(
      this.#natalInput,
      targetDate,
      targetTimeIndex,
      engine,
      natalTopics,
      horoscopeTopics,
      options,
    );
    if (!result.ok) {
      this.#timingResult = undefined;
      this.#timingIssues = Object.freeze([...result.issues]);
      return this.snapshot();
    }
    this.#timingResult = result;
    this.#timingIssues = Object.freeze([]);
    this.#navigation = navigateApp(this.#navigation, 'natal_timing').state;
    return this.snapshot();
  }

  saveCurrentNatal(
    repository: LocalChartRepository,
    savedAt: string,
    options: Readonly<{ alias?: string }> = {},
  ): SaveProductionNatalChartResult {
    if (!this.#natalResult) throw new Error('Saving requires a successful natal result first');
    return saveProductionNatalChart(repository, this.#natalResult.chart, savedAt, {
      ...(options.alias !== undefined ? { alias: options.alias } : {}),
      ...(this.#natalResult.chronology ? { chronology: this.#natalResult.chronology.birth } : {}),
    });
  }

  buildCurrentNatalPrint(action: ProductionPrintActionId): PrintPacket {
    if (!this.#natalResult) throw new Error('Printing requires a successful natal result first');
    return buildProductionPrintPacket(
      action,
      this.#natalResult.chart,
      this.#natalResult.report.topics,
      this.#natalResult.chronology,
    );
  }

  clearNatalTimingResult(): ProductionAppSnapshot {
    this.#timingResult = undefined;
    this.#timingIssues = Object.freeze([]);
    return this.snapshot();
  }

  async submitRelationship(
    personAInput: BirthInputDraft,
    personBInput: BirthInputDraft,
    relationshipType: RelationshipContextType,
    engine: CalculationEngine,
    options: ApplicationWorkflowOptions = {},
  ): Promise<ProductionAppSnapshot> {
    const result = await runRelationshipWorkflow(personAInput, personBInput, relationshipType, engine, undefined, options);
    if (!result.ok) {
      this.#relationshipResult = undefined;
      this.#relationshipIssues = Object.freeze({
        personA: Object.freeze([...result.personAIssues]),
        personB: Object.freeze([...result.personBIssues]),
      });
      this.#navigation = setRelationshipResultAvailability(this.#navigation, false);
      this.#navigation = navigateApp(this.#navigation, 'relationship').state;
      return this.snapshot();
    }
    this.#relationshipResult = result;
    this.#relationshipIssues = Object.freeze({ personA: Object.freeze([]), personB: Object.freeze([]) });
    this.#navigation = setRelationshipResultAvailability(this.#navigation, true);
    this.#navigation = navigateApp(this.#navigation, 'relationship_result').state;
    return this.snapshot();
  }

  clearRelationshipResult(): ProductionAppSnapshot {
    this.#relationshipResult = undefined;
    this.#relationshipIssues = Object.freeze({ personA: Object.freeze([]), personB: Object.freeze([]) });
    this.#navigation = setRelationshipResultAvailability(this.#navigation, false);
    return this.snapshot();
  }

  clearNatalResult(): ProductionAppSnapshot {
    this.#natalResult = undefined;
    this.#natalInput = undefined;
    this.#timingResult = undefined;
    this.#timingIssues = Object.freeze([]);
    this.#natalIssues = Object.freeze([]);
    this.#navigation = setNatalResultAvailability(this.#navigation, false);
    return this.snapshot();
  }

  #buildNatalResultView(result: ProductionNatalWorkflowSuccess): ProductionNatalResultPageViewModel {
    return buildProductionNatalResultPageViewModel({
      chart: result.chart,
      report: result.report,
      summary: result.summary,
      overviewDigest: buildOverviewDigest(result.report),
      actions: buildResultActionsViewModel(),
      topicCoverage: result.topicCoverage,
      ...(result.chronology ? { chronology: result.chronology } : {}),
    });
  }
}
